import React, { useEffect, useRef, useState } from 'react';
import { TooltipProvider, Tooltip as UITooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { CircleHelp } from 'lucide-react';
import { useTranslation } from "react-i18next";
import axiosInstance from '@/api/axios.js';
import * as d3 from 'd3';

const CostSavingsRadialChart = ({ selectedBins, selectedPeriod }) => {
    const svgRef = useRef(null);
    const containerRef = useRef(null);
    const tooltipRef = useRef(null);
    const [chartData, setChartData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
    const [isDarkMode, setIsDarkMode] = useState(false);
    const { t } = useTranslation();

    // Detect dark mode
    useEffect(() => {
        const checkDarkMode = () => {
            // Check if dark mode is set via class on html element
            const isDark = document.documentElement.classList.contains('dark');
            setIsDarkMode(isDark);
        };
        
        // Initial check
        checkDarkMode();
        
        // Observe for changes
        const observer = new MutationObserver(checkDarkMode);
        observer.observe(document.documentElement, { 
            attributes: true, 
            attributeFilter: ['class'] 
        });
        
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            try {
                const params = {
                    timeRange: selectedPeriod || 'Year',
                    bins: selectedBins.join(',')
                };
                const response = await axiosInstance.get('cost_savings_page', { params });
                setChartData(response.data);
            } catch (error) {
                console.error("Error fetching cost savings data:", error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchData();
    }, [selectedBins, selectedPeriod]);

    // Handle resizing
    useEffect(() => {
        const updateDimensions = () => {
            if (containerRef.current) {
                const { width, height } = containerRef.current.getBoundingClientRect();
                setDimensions({ width, height });
            }
        };
        updateDimensions();
        window.addEventListener('resize', updateDimensions);
        return () => window.removeEventListener('resize', updateDimensions);
    }, []);

    useEffect(() => {
        if (!chartData || isLoading || dimensions.width === 0) return;

        // Clear previous chart
        d3.select(svgRef.current).selectAll("*").remove();

        const safeData = chartData?.total_savings || chartData?.total_savings_legacy || {};
        const normalizedData = {
            PMD: safeData.PMD || safeData.pmd || 0,
            organic: safeData.organic || 0,
            paper: safeData.paper || 0,
        };

        const getLocalizedWasteName = (waste) => {
            const wasteKey = waste === "PMD" ? "pmd" : waste.toLowerCase();
            return t(`costSavings.wasteTypes.${wasteKey}`, { defaultValue: waste === "PMD" ? "PMD" : waste.charAt(0).toUpperCase() + waste.slice(1) });
        };

        const names = Object.keys(normalizedData).map(getLocalizedWasteName);
        const values = Object.values(normalizedData);
        const total = values.reduce((sum, val) => sum + val, 0);

        // Set up dimensions
        const width = dimensions.width;
        const height = dimensions.height;
        const size = Math.min(width, height);
        const radius = size / 2 - 20;
        const centerX = width / 2;
        const centerY = height / 2;

        const svg = d3.select(svgRef.current)
            .attr("width", width)
            .attr("height", height);

        // Keep your original colors
        const palette = [
            "#fbbf24", // Yellow for PMD
            "#10b981", // Green for Organic
            "#3b82f6"  // Blue for Paper
        ];

        // Colors that adapt to dark mode
        const backgroundColor = isDarkMode ? "#374151" : "#F0F1F2";
        const textColor = isDarkMode ? "#E5E7EB" : "#000";
        const mutedTextColor = isDarkMode ? "#9CA3AF" : "#6B7280";
        const totalTextColor = isDarkMode ? "#F9FAFB" : "#111827";
        const totalHoverColor = isDarkMode ? "#34D399" : "#059669";
        
        const tooltipBgColor = isDarkMode ? "#1F2937" : "white";
        const tooltipTextColor = isDarkMode ? "#F3F4F6" : "inherit";
        const tooltipBorderColor = isDarkMode ? "#4B5563" : "#e5e7eb";

        const maxValue = Math.max(...values, 1);

        const startAngle = 0 * (Math.PI / 180); // 0 degrees, top
        const endAngle = 270 * (Math.PI / 180); // 270 degrees, clockwise

        // Create tooltip
        const tooltip = d3.select(tooltipRef.current);

        names.forEach((name, index) => {
            const barRadius = radius - (index * (radius / 5));
            const barWidth = Math.min(24, size / 15); 
            
            // Draw background bar
            const backgroundArc = d3.arc()
                .innerRadius(barRadius - barWidth / 2)
                .outerRadius(barRadius + barWidth / 2)
                .startAngle(startAngle)
                .endAngle(endAngle);

            svg.append("path")
                .attr("d", backgroundArc)
                .attr("transform", `translate(${centerX}, ${centerY})`)
                .attr("fill", backgroundColor)
                .attr("stroke", "none");

            // Draw colored bar (based on value)
            const percentage = (values[index] / maxValue);
            const sweepAngle = startAngle + (endAngle - startAngle) * percentage;

            const valueArc = d3.arc()
                .innerRadius(barRadius - barWidth / 2)
                .outerRadius(barRadius + barWidth / 2)
                .startAngle(startAngle)
                .endAngle(sweepAngle);

            const barPath = svg.append("path")
                .attr("d", valueArc)
                .attr("transform", `translate(${centerX}, ${centerY})`)
                .attr("fill", palette[index])
                .attr("stroke", "none")
                .style("cursor", "pointer")
                .style("transition", "all 0.3s ease");

            // Add hover effects to the colored bar
            barPath
                .on("mouseover", function(event) {
                    // Brighten the color on hover
                    d3.select(this)
                        .transition()
                        .duration(200)
                        .style("filter", "brightness(1.2) drop-shadow(0px 2px 4px rgba(0,0,0,0.2))");

                    // Show tooltip
                    const [mouseX, mouseY] = d3.pointer(event, document.body);
                    const percentOfMax = ((values[index] / maxValue) * 100).toFixed(1);
                    const percentOfTotal = ((values[index] / total) * 100).toFixed(1);
                    
                    tooltip
                        .style("opacity", 1)
                        .style("left", `${mouseX + 10}px`)
                        .style("top", `${mouseY - 10}px`)
                        .style("background-color", tooltipBgColor)
                        .style("color", tooltipTextColor)
                        .style("border-color", tooltipBorderColor)
                        .html(`
                            <div style="font-weight: 600;">${name}</div>
                            <div>Amount: €${values[index].toFixed(2)}</div>
                            <div>${percentOfMax}% of maximum</div>
                            <div>${percentOfTotal}% of total</div>
                        `);
                })
                .on("mousemove", function(event) {
                    const [mouseX, mouseY] = d3.pointer(event, document.body);
                    tooltip
                        .style("left", `${mouseX + 10}px`)
                        .style("top", `${mouseY - 10}px`);
                })
                .on("mouseout", function() {
                    // Reset filter on mouse out
                    d3.select(this)
                        .transition()
                        .duration(200)
                        .style("filter", "none");

                    // Hide tooltip
                    tooltip.style("opacity", 0);
                });

            // Add labels with hover effects
            const labelOffsetX = -80;
            const labelOffsetY = 20;
            const labelRadius = barRadius + 20;
            const x = centerX + labelRadius * Math.cos(startAngle - Math.PI / 2) + labelOffsetX;
            const y = centerY + labelRadius * Math.sin(startAngle - Math.PI / 2) + labelOffsetY;

            const label = svg.append("text")
                .attr("x", x)
                .attr("y", y)
                .attr("text-anchor", "start")
                .attr("dominant-baseline", "middle")
                .text(`${name}: €${values[index].toFixed(2)}`)
                .style("font-size", "11px")
                .style("font-weight", "bold")
                .style("cursor", "pointer")
                .style("transition", "all 0.3s ease")
                .style("fill", textColor);

            // Add hover effects to labels
            label
                .on("mouseover", function(event) {
                    d3.select(this)
                        .transition()
                        .duration(200)
                        .style("font-size", "12px")
                        .style("fill", palette[index]);

                    // Also highlight the corresponding bar
                    barPath
                        .transition()
                        .duration(200)
                        .style("filter", "brightness(1.2) drop-shadow(0px 2px 4px rgba(0,0,0,0.2))");

                    // Show tooltip
                    const [mouseX, mouseY] = d3.pointer(event, document.body);
                    const percentOfMax = ((values[index] / maxValue) * 100).toFixed(1);
                    const percentOfTotal = ((values[index] / total) * 100).toFixed(1);
                    
                    tooltip
                        .style("opacity", 1)
                        .style("left", `${mouseX + 10}px`)
                        .style("top", `${mouseY - 10}px`)
                        .style("background-color", tooltipBgColor)
                        .style("color", tooltipTextColor)
                        .style("border-color", tooltipBorderColor)
                        .html(`
                            <div style="font-weight: 600;">${name}</div>
                            <div>Amount: €${values[index].toFixed(2)}</div>
                            <div>${percentOfMax}% of maximum</div>
                            <div>${percentOfTotal}% of total</div>
                        `);
                })
                .on("mousemove", function(event) {
                    const [mouseX, mouseY] = d3.pointer(event, document.body);
                    tooltip
                        .style("left", `${mouseX + 10}px`)
                        .style("top", `${mouseY - 10}px`);
                })
                .on("mouseout", function() {
                    d3.select(this)
                        .transition()
                        .duration(200)
                        .style("font-size", "11px")
                        .style("fill", textColor);

                    // Reset bar filter
                    barPath
                        .transition()
                        .duration(200)
                        .style("filter", "none");

                    // Hide tooltip
                    tooltip.style("opacity", 0);
                });
        });

        // Add total savings in center with hover effect
        const totalGroup = svg.append("g")
            .style("cursor", "pointer");

        totalGroup.append("text")
            .attr("x", centerX)
            .attr("y", centerY - 10)
            .attr("text-anchor", "middle")
            .attr("dominant-baseline", "middle")
            .text(t('costSavings.totalSavings'))
            .style("font-size", "13px")
            .style("fill", mutedTextColor);

        const totalValueText = totalGroup.append("text")
            .attr("x", centerX)
            .attr("y", centerY + 10)
            .attr("text-anchor", "middle")
            .attr("dominant-baseline", "middle")
            .text(`€${total.toFixed(2)}`)
            .style("font-size", "16px")
            .style("font-weight", "bold")
            .style("fill", totalTextColor);

        // Add hover effect to total savings
        totalGroup
            .on("mouseover", function(event) {
                totalValueText
                    .transition()
                    .duration(200)
                    .style("font-size", "18px")
                    .style("fill", totalHoverColor);

                // Show tooltip for total
                const [mouseX, mouseY] = d3.pointer(event, document.body);
                tooltip
                    .style("opacity", 1)
                    .style("left", `${mouseX + 10}px`)
                    .style("top", `${mouseY - 10}px`)
                    .style("background-color", tooltipBgColor)
                    .style("color", tooltipTextColor)
                    .style("border-color", tooltipBorderColor)
                    .html(`
                        <div style="font-weight: 600;">${t('costSavings.totalSavings')}</div>
                        <div>€${total.toFixed(2)}</div>
                        <div>${names[0]}: €${values[0].toFixed(2)} (${((values[0]/total)*100).toFixed(1)}%)</div>
                        <div>${names[1]}: €${values[1].toFixed(2)} (${((values[1]/total)*100).toFixed(1)}%)</div>
                        <div>${names[2]}: €${values[2].toFixed(2)} (${((values[2]/total)*100).toFixed(1)}%)</div>
                    `);
            })
            .on("mousemove", function(event) {
                const [mouseX, mouseY] = d3.pointer(event, document.body);
                tooltip
                    .style("left", `${mouseX + 10}px`)
                    .style("top", `${mouseY - 10}px`);
            })
            .on("mouseout", function() {
                totalValueText
                    .transition()
                    .duration(200)
                    .style("font-size", "16px")
                    .style("fill", totalTextColor);

                tooltip.style("opacity", 0);
            });

    }, [chartData, isLoading, t, dimensions, isDarkMode]);

    return (
        <div className="sm:rounded-lg bg-muted/50 p-4 h-full flex flex-col">
            <div className="flex items-center justify-between mb-4">
                <h4 className="scroll-m-20 text-xl font-semibold tracking-tight">
                    {t('costSavings.title')}
                </h4>
                <TooltipProvider>
                    <UITooltip>
                        <TooltipTrigger asChild>
                            <CircleHelp className="h-5 w-5 text-muted-foreground cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent>
                            <p className="max-w-xs">
                                This chart shows your cost savings from recycling different types of waste.<br/>
                                Each circular bar represents a waste category (PMD, Organic, Paper) and shows how much money you've saved compared to landfill disposal costs.
                            </p>
                        </TooltipContent>
                    </UITooltip>
                </TooltipProvider>
            </div>
            <div className="flex-grow" ref={containerRef}>
                {isLoading ? (
                    <div className="w-full h-full flex items-center justify-center">
                        {t('costSavings.loading')}
                    </div>
                ) : (
                    <svg ref={svgRef} className="w-full h-full" style={{ minHeight: '200px' }} />
                )}
            </div>
            
            {/* Tooltip */}
            <div 
                ref={tooltipRef}
                style={{ 
                    position: 'fixed',
                    pointerEvents: 'none',
                    backgroundColor: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                    padding: '12px',
                    fontSize: '14px',
                    zIndex: 1000,
                    opacity: 0,
                    transition: 'opacity 0.2s',
                    transform: 'translate(-50%, -100%)'
                }}
            />
        </div>
    );
};

export default CostSavingsRadialChart;